import { Button, Card } from "@blueprintjs/core"
import { cloneDeep, get, isEmpty, omit, set, update } from "lodash-es"
import React, { useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import useRouteForm from "hooks/useRouteForm"
import FormController from "components/Molecules/FormController"
import MutationOptions from "components/Organisms/MutationOptions"
import AppToaster from "utils/AppToaster"
import {
  ADD_UPDATE_VARIETY,
  VARIETY_DETAILS,
  VARIETY_LIST,
} from "graphql/crop.graphql"
import { useFormDisabled } from "hooks/useAuth"
import { useAuthorizationSimple } from "hooks/useAuthorizationStrict"
import useErrors from "hooks/useErrors"
import varietyModel from "models/molecules/variety.model"
import { ErrorToast } from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import { graphqlError } from "utils/parseError"
import CropDetails from "./CropDetails"
import HarvestGrades from "./HarvestGrades"
import Parameters from "./Parameters"
import { infoType } from "./ParamterValues"

function CropRegistrationForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const canAddCrop = useAuthorizationSimple("vt_add_crop")
  const canEditCrop = useAuthorizationSimple("vt_edit_crop")
  const [varietyDetails, changeVarietyDetails] = useRouteForm(VARIETY_DETAILS, {
    default: { qualityParameters: [], harvestGrades: [] },
    transform(state) {
      return {
        ...state,
        qualityParameters: state?.qualityParameters?.map(qp => ({
          ...qp,
          valueType: infoType.find(i => i.id === qp.valueType),
        })),
      }
    },
  })

  function showToast(data) {
    const variety = get(data, "addUpdateCropVariety.variety")
    AppToaster.show(
      {
        message: `Variety ${variety.name} ${
          varietyDetails.id ? "Updated" : "Added"
        } Successfully`,
        intent: "success",
      },
      variety.id
    )
    window.scrollTo(0, 0)
  }
  function handleChange(path, value) {
    const varietyDetailsCopy = cloneDeep(varietyDetails)
    set(varietyDetailsCopy, path, value)
    changeVarietyDetails(varietyDetailsCopy)
  }
  function handleRemove(path, index) {
    const varietyDetailsCopy = cloneDeep(varietyDetails)
    update(varietyDetailsCopy, path, arr => arr.filter((_, i) => i !== index))
    changeVarietyDetails(varietyDetailsCopy)
  }
  function handleAdd(path, value) {
    const varietyDetailsCopy = cloneDeep(varietyDetails)
    update(varietyDetailsCopy, path, arr => arr.concat(value))
    changeVarietyDetails(varietyDetailsCopy)
  }

  const parsedVariables = useMemo(() => {
    return {
      id: varietyDetails?.id,
      name: varietyDetails?.name,
      estimatedProduce: varietyDetails?.estimatedProduce,
      standardDeduction: varietyDetails?.standardDeduction || 0,
      harvestGrades: varietyDetails?.harvestGrades?.map(hg => ({
        id: hg?.id,
        specifications: hg?.specifications,
        name: hg?.name?.name ?? hg?.name,
      })),
      qualityParameters: varietyDetails?.qualityParameters?.map(qp => ({
        ...omit(qp, ["__typename"]),
        valueType: qp.valueType?.id,
      })),
      preferredCertification: varietyDetails?.preferredCertification?.id,
      primaryGroupId: varietyDetails?.primaryGroup?.id,
      preferredSku: varietyDetails?.preferredSku?.id,
      groups: varietyDetails?.groups?.map(getId),
      commodity: varietyDetails?.commodity?.id,
    }
  }, [varietyDetails])

  const [errors] = useErrors({ schema: varietyModel, state: parsedVariables })

  const disabled = useFormDisabled("crop", parsedVariables)

  return (
    <Card className="vt-crop-registration">
      <div className="mt-2 w-full flex items-center">
        <span>
          <Button
            icon="arrow-left"
            onClick={() => navigate(-1)}
            minimal={true}
          ></Button>
        </span>
        <h5 className="ml-3">{parseInt(id) ? "Edit" : "New"} Crop Variety</h5>
      </div>
      <div className="container mt-3">
        <FormController
          source={varietyDetails}
          errors={errors.details}
          onChange={handleChange}
          disableAll={true}
          disabled={disabled}
          onAdd={handleAdd}
          onRemove={handleRemove}
        >
          <CropDetails />
          <Parameters path="qualityParameters"></Parameters>
          <HarvestGrades path="harvestGrades"></HarvestGrades>
        </FormController>
        <MutationOptions
          disabled={disabled}
          validate={state => {
            if (state.id && !canEditCrop) {
              return ErrorToast(
                "You do not have the permission to edit crop, please contact your admin"
              )
            }

            if (!state.id && !canAddCrop) {
              return ErrorToast(
                "You do not have the permission to add a crop, please contact your admin"
              )
            }
            if (isEmpty(errors)) return true
            AppToaster.show({
              message: errors.latest,
              intent: "danger",
            })
            return false
          }}
          refetchQueries={[
            {
              query: VARIETY_LIST,
              variables: {
                query: "",
                first: 20,
                skip: 0,
              },
            },
          ]}
          onError={error => {
            AppToaster.show({
              message: graphqlError(error.message),
              intent: "danger",
            })
          }}
          className="mt-5"
          onSave={data => {
            showToast(data)
            navigate(-1)
          }}
          onSaveAndEdit={data => {
            const varietyId = data?.addUpdateCropVariety?.variety?.id
            if (!parseInt(id)) {
              navigate(`/d/registration/crop/${varietyId}`, { replace: true })
            }
            showToast(data)
          }}
          onSaveAndAdd={data => {
            showToast(data)
            changeVarietyDetails({
              qualityParameters: [],
              harvestGrades: [],
            })
          }}
          mutation={ADD_UPDATE_VARIETY}
          variables={parsedVariables}
        ></MutationOptions>
      </div>
    </Card>
  )
}

export default CropRegistrationForm
